import React from 'react';

import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';

import { resetServiceFees } from '~/services/BusinessTraveller/BusinessTravellerService';

interface Props {
  businessId: string;
  serviceFeeCount: number;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function BusinessServiceFeesCreateEditModal({
  businessId,
  isOpen,
  onClose,
  onSuccess,
  serviceFeeCount,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const resetMutation = useMutation({
    mutationFn: async () => {
      return resetServiceFees(businessId);
    },
    onSuccess: () => {
      enqueueSnackbar('Service fees reset successfully', { variant: 'success' });
      onSuccess();
      onClose();
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : JSON.stringify(error, null, 2);
      enqueueSnackbar(`Error resetting service fee [${message}]`, { variant: 'error' });
    },
  });

  const handleReset = () => {
    if (!businessId || businessId.trim().length === 0) {
      enqueueSnackbar('Invalid businessId provided', { variant: 'error' });
      return;
    }
    resetMutation.mutate();
  };

  const isLoading = resetMutation.isPending;

  return (
    <Dialog open={isOpen} onClose={isLoading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{serviceFeeCount > 0 ? 'Reset To Default' : 'Add Default Fees'}</DialogTitle>
      <DialogContent>
        {serviceFeeCount === 0 && (
          <Stack gap={2} alignItems="start">
            <Typography variant="body2" gutterBottom>
              Do you want to add default service fees?
            </Typography>
          </Stack>
        )}
        {serviceFeeCount > 0 && (
          <Stack gap={2} alignItems="start">
            <Typography variant="body2" gutterBottom>
              Do you want to reset to the default service fees?
            </Typography>
            <Typography variant="body2">
              This will delete all existing service fees and create new ones with default values.
            </Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button disabled={isLoading} variant="text" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleReset} loading={isLoading} disabled={isLoading} variant="contained" color="primary">
          {serviceFeeCount > 0 ? 'Reset' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
